import type { HttpError } from '@refinedev/core'
import { useSelect } from '@refinedev/core'
import type {
  Cabinet,
  DashboardPeriod,
  Location,
} from '@sirius/contracts/admin'
import { DASHBOARD_PERIODS } from '@sirius/contracts/admin'
import { X } from 'lucide-react'
import { useId, useMemo, useState, useTransition } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { ListView, ListViewHeader } from '~/components/refineUi/views/listView'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { cn } from '~/lib/utils'
import { useActiveTenant } from '~/tenancy/activeTenant'
import { DashboardCard } from './dashboardCard'
import type { DashboardFilters } from './dashboardFilters'
import {
  dashboardInput,
  isBerlinDate,
  MIN_YEAR,
  parseDashboardFilters,
  serializeDashboardFilters,
  withCabinet,
  withLocation,
  withPeriod,
  withUntil,
} from './dashboardFilters'
import { periodDescription, periodLabel } from './dashboardFormat'

/** "All …" in a select: a Radix Select item cannot hold an empty value. */
const ALL = '__all__'

/** Refine's select options as the filter selects take them (string values). */
const toOptions = (options: { label: string; value: unknown }[]) =>
  options.map(({ label, value }) => ({ label, value: String(value) }))

const isPeriod = (value: string): value is DashboardPeriod =>
  (DASHBOARD_PERIODS as readonly string[]).includes(value)

function FilterSelect({
  label,
  value,
  allLabel,
  options,
  width = 'sm:w-40',
  onChange,
}: {
  label: string
  /** the trigger's width from `sm` up (full width below) */
  width?: string
  value: string
  /** the "All …" entry, if any */
  allLabel?: string
  options: readonly { value: string; label: string }[]
  onChange: (value: string) => void
}) {
  const id = useId()
  return (
    <div className="grid w-full gap-1.5 sm:w-auto">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value}
        onValueChange={onChange}
      >
        <SelectTrigger
          id={id}
          className={cn('w-full', width)}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allLabel === undefined ? null : (
            <SelectItem value={ALL}>{allLabel}</SelectItem>
          )}
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

/**
 * The Until date. It keeps what is being typed in its own state: the URL
 * changes in a transition, so a field bound to it would get its old value back
 * after every keystroke, and a date input reports a half-typed date (a year
 * typed digit by digit: `0002-01-14`). Only a complete date goes to the URL;
 * clearing the field takes effect when it loses focus ("Until now" clears it
 * at once).
 */
function UntilInput({
  id,
  until,
  onCommit,
}: {
  id: string
  until: string | undefined
  onCommit: (until: string | undefined) => void
}) {
  const [text, setText] = useState(until ?? '')
  const [shown, setShown] = useState(until)
  if (shown !== until) {
    // the URL changed elsewhere (Until now, back/forward)
    setShown(until)
    setText(until ?? '')
  }
  return (
    <Input
      id={id}
      type="date"
      min={`${MIN_YEAR}-01-01`}
      className="w-full sm:w-40"
      value={text}
      onChange={(event) => {
        const next = event.target.value
        setText(next)
        if (isBerlinDate(next) && next !== until) {
          onCommit(next)
        }
      }}
      onBlur={() => {
        if (text === '' && until !== undefined) {
          onCommit(undefined)
        } else if (!isBerlinDate(text)) {
          setText(until ?? '')
        }
      }}
    />
  )
}

/**
 * The dashboard (`/`): profit, credits on the cabinets and active cabinets
 * over 24 hours, 7 or 30 days of the host's tenant (staff included, as on
 * every page), for staff and the tenant's owners and admins
 * (`DASHBOARD_VIEWER_ROLES`; the route sends members to the locations).
 * Filters ride in the URL (`dashboardFilters.ts`).
 */
export function DashboardPage() {
  const host = useActiveTenant()
  const { t } = useTranslation()
  const untilId = useId()
  const [searchParams, setSearchParams] = useSearchParams()
  // a filter change suspends the cards on their new query: in a transition
  // they keep showing the old numbers (dimmed) instead of skeletons
  const [isPending, startTransition] = useTransition()
  const filters = useMemo(
    () => parseDashboardFilters(searchParams),
    [searchParams],
  )
  const input = useMemo(
    () => dashboardInput(filters, host.id),
    [filters, host.id],
  )
  const update = (next: DashboardFilters) => {
    startTransition(() => {
      setSearchParams(serializeDashboardFilters(next))
    })
  }

  // the host tenant's locations and cabinets (the data provider scopes them)
  const locations = useSelect<Location, HttpError>({
    resource: 'locations',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
  })
  const cabinets = useSelect<Cabinet, HttpError>({
    resource: 'cabinets',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
    filters:
      filters.location === undefined
        ? []
        : [{ field: 'locationId', operator: 'eq', value: filters.location }],
  })
  const selectedCabinetLocation = cabinets.query.data?.data.find(
    (cabinet) => cabinet.id === filters.cabinet,
  )?.locationId

  return (
    <ListView>
      <ListViewHeader
        resource="dashboard"
        title={t('dashboardPage.title')}
      />
      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label={t('dashboardPage.filters.period')}
          width="sm:w-32"
          value={filters.period}
          options={DASHBOARD_PERIODS.map((period) => ({
            value: period,
            label: periodLabel(t, period),
          }))}
          onChange={(value) => {
            if (isPeriod(value)) {
              update(withPeriod(filters, value))
            }
          }}
        />
        <div className="grid w-full gap-1.5 sm:w-auto">
          <Label htmlFor={untilId}>{t('dashboardPage.filters.until')}</Label>
          <div className="flex gap-1">
            <UntilInput
              id={untilId}
              until={filters.until}
              onCommit={(until) => {
                update(withUntil(filters, until))
              }}
            />
            {filters.until === undefined ? null : (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('dashboardPage.filters.untilNow')}
                title={t('dashboardPage.filters.untilNow')}
                onClick={() => {
                  update(withUntil(filters, undefined))
                }}
              >
                <X />
              </Button>
            )}
          </div>
        </div>
        <FilterSelect
          label={t('dashboardPage.filters.location')}
          width="sm:w-52"
          value={filters.location ?? ALL}
          allLabel={t('dashboardPage.filters.allLocations')}
          options={toOptions(locations.options)}
          onChange={(value) => {
            update(
              withLocation(
                filters,
                value === ALL ? undefined : value,
                selectedCabinetLocation,
              ),
            )
          }}
        />
        <FilterSelect
          label={t('dashboardPage.filters.cabinet')}
          width="sm:w-36"
          value={filters.cabinet ?? ALL}
          allLabel={t('dashboardPage.filters.allCabinets')}
          options={toOptions(cabinets.options)}
          onChange={(value) => {
            update(withCabinet(filters, value === ALL ? undefined : value))
          }}
        />
      </div>
      <p className="text-muted-foreground text-sm">
        {t('dashboardPage.window.berlinTime', {
          window: periodDescription(t, filters.period, filters.until),
        })}
      </p>
      <div
        aria-busy={isPending}
        className={cn(
          'grid gap-4 lg:grid-cols-3',
          isPending && 'opacity-60 transition-opacity',
        )}
      >
        <DashboardCard
          kind="profit"
          input={input}
        />
        <DashboardCard
          kind="credits"
          input={input}
        />
        <DashboardCard
          kind="activeCabinets"
          input={input}
        />
      </div>
    </ListView>
  )
}
